import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { client } from "@/services/api/client.gen";
import { useAuth } from "@/contexts/AuthContext";
import { useDebounce } from "./useDebounce";
import { useSearchFilters } from "./useSearchFilters";
import {
  SEARCH_DEFAULTS,
  SEARCH_API_PATHS,
  SORT_BY,
  SORT_BY_TO_API,
  SEARCH_MODES,
  DATE_FILTERS,
  SORT_ORDER,
  PAGE_SIZE_OPTIONS,
} from "@/constants/search";

// localStorage key for persisting page size preference
const PAGE_SIZE_STORAGE_KEY = "studio1:pageSize";
import type {
  SearchResultItem,
  SearchResponse,
  SearchPaginationMeta,
} from "@/types/search";

/**
 * URL parameter keys for search state synchronization.
 * Maps internal state names to URL-friendly keys.
 */
const URL_KEYS = {
  query: "q",
  attributes: "attrs",
  entityTypes: "types",
  dateFilter: "date",
  sortBy: "sort",
  sortOrder: "order",
  searchMode: "mode",
  page: "page",
  limit: "limit",
} as const;

/**
 * Parse initial state from URL parameters.
 * Returns undefined for missing params (will use API defaults).
 */
function parseUrlState(searchParams: URLSearchParams) {
  const getString = (key: string): string | undefined => {
    const value = searchParams.get(key);
    return value || undefined;
  };

  const getArray = (key: string): string[] | undefined => {
    const value = searchParams.get(key);
    if (!value) return undefined;
    const arr = value.split(",").filter(Boolean);
    return arr.length > 0 ? arr : undefined;
  };

  const getNumber = (key: string): number | undefined => {
    const value = searchParams.get(key);
    if (!value) return undefined;
    const num = parseInt(value, 10);
    return isNaN(num) ? undefined : num;
  };

  return {
    query: getString(URL_KEYS.query),
    attributes: getArray(URL_KEYS.attributes),
    entityTypes: getArray(URL_KEYS.entityTypes),
    dateFilter: getString(URL_KEYS.dateFilter),
    sortBy: getString(URL_KEYS.sortBy),
    sortOrder: getString(URL_KEYS.sortOrder) as "asc" | "desc" | undefined,
    searchMode: getString(URL_KEYS.searchMode),
    page: getNumber(URL_KEYS.page),
    limit: getNumber(URL_KEYS.limit),
  };
}

/**
 * Get initial page size from URL params > localStorage > default
 */
function getInitialPageSize(searchParams: URLSearchParams): number {
  // 1. Check URL params first
  const urlLimit = searchParams.get(URL_KEYS.limit);
  if (urlLimit) {
    const parsed = parseInt(urlLimit, 10);
    if (
      PAGE_SIZE_OPTIONS.includes(parsed as (typeof PAGE_SIZE_OPTIONS)[number])
    ) {
      return parsed;
    }
  }

  // 2. Check localStorage
  try {
    const stored = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (
        PAGE_SIZE_OPTIONS.includes(parsed as (typeof PAGE_SIZE_OPTIONS)[number])
      ) {
        return parsed;
      }
    }
  } catch {
    // localStorage not available
  }

  // 3. Fall back to default
  return SEARCH_DEFAULTS.LIMIT;
}

/**
 * Check if two arrays are equal (shallow, order-insensitive)
 */
function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((v, i) => v === sortedB[i]);
}

/**
 * Main search hook for the Creative Dashboard.
 *
 * Manages search state, debouncing, filter defaults, and TanStack Query
 * for fetching search results from GET /v1/search.
 *
 * URL State Synchronization:
 * - Reads initial state from URL on mount (before API defaults)
 * - Writes state changes to URL (debounced for search query)
 * - Omits default values from URL to keep it clean
 * - Supports browser back/forward navigation
 *
 * @param projectId - Optional project ID to scope search to workflows within a project
 */
export function useSearch(projectId?: string | null) {
  const { user } = useAuth();
  const { filters, defaults, isLoading: isLoadingFilters } = useSearchFilters();
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse URL state once on mount (before defaults load)
  const initialUrlState = useMemo(
    () => parseUrlState(searchParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [], // Intentionally empty - only parse on mount
  );

  // Track if we've hydrated from URL
  const [isHydrated, setIsHydrated] = useState(false);

  // Search text state - initialized from URL if present
  const [searchQuery, setSearchQueryInternal] = useState(
    initialUrlState.query ?? "",
  );
  const debouncedSearch = useDebounce(searchQuery, SEARCH_DEFAULTS.DEBOUNCE_MS);

  // Pagination state - initialized from URL > localStorage > default
  const [page, setPageInternal] = useState<number>(
    initialUrlState.page ?? SEARCH_DEFAULTS.PAGE,
  );
  const [limit, setLimitState] = useState<number>(() =>
    getInitialPageSize(searchParams),
  );

  // Advanced filter state - initialized from URL if present, otherwise will sync from API defaults
  const [selectedAttributes, setSelectedAttributesInternal] = useState<
    string[]
  >(initialUrlState.attributes ?? []);
  const [selectedEntityTypes, setSelectedEntityTypesInternal] = useState<
    string[]
  >(initialUrlState.entityTypes ?? []);
  const [dateFilter, setDateFilterInternal] = useState<string>(
    initialUrlState.dateFilter ?? DATE_FILTERS.ALL_TIME,
  );
  const [sortBy, setSortByInternal] = useState<string>(
    initialUrlState.sortBy ?? SORT_BY.UPDATED_AT,
  );
  const [sortOrder, setSortOrderInternal] = useState<"asc" | "desc">(
    initialUrlState.sortOrder ?? SORT_ORDER.DESC,
  );
  const [searchMode, setSearchModeInternal] = useState<string>(
    initialUrlState.searchMode ?? SEARCH_MODES.SMART,
  );
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

  // Track debounce timer for search query URL updates
  const queryUrlTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync state with defaults once they load from the API
  // BUT only if URL didn't already provide those values
  const [defaultsSynced, setDefaultsSynced] = useState(false);
  useEffect(() => {
    if (!isLoadingFilters && !defaultsSynced) {
      // Only apply API defaults if URL didn't specify these values
      if (initialUrlState.attributes === undefined) {
        setSelectedAttributesInternal(defaults.attributes);
      }
      if (initialUrlState.entityTypes === undefined) {
        setSelectedEntityTypesInternal(defaults.entityTypes);
      }
      if (initialUrlState.dateFilter === undefined) {
        setDateFilterInternal(defaults.dateFilter);
      }
      if (initialUrlState.sortBy === undefined) {
        setSortByInternal(defaults.sortBy);
      }
      if (initialUrlState.searchMode === undefined) {
        setSearchModeInternal(defaults.searchMode);
      }
      setDefaultsSynced(true);
      setIsHydrated(true);
    }
  }, [isLoadingFilters, defaultsSynced, defaults, initialUrlState]);

  // ============================================================================
  // URL Sync Helpers
  // ============================================================================

  /**
   * Update URL params with the given changes.
   * Omits values that match defaults.
   */
  const updateUrl = useCallback(
    (
      updates: Partial<Record<keyof typeof URL_KEYS, string | undefined>>,
      options?: { replace?: boolean },
    ) => {
      const shouldReplace = options?.replace ?? true;

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);

          for (const [stateKey, value] of Object.entries(updates)) {
            const urlKey = URL_KEYS[stateKey as keyof typeof URL_KEYS];
            if (!urlKey) continue;

            if (value === undefined || value === "") {
              next.delete(urlKey);
            } else {
              next.set(urlKey, value);
            }
          }

          return next;
        },
        { replace: shouldReplace },
      );
    },
    [setSearchParams],
  );

  /**
   * Serialize array to URL format, or undefined if matches default
   */
  const serializeArray = (
    arr: string[],
    defaultArr: string[],
  ): string | undefined => {
    if (arraysEqual(arr, defaultArr) || arr.length === 0) {
      return undefined;
    }
    return arr.join(",");
  };

  /**
   * Serialize value to URL format, or undefined if matches default
   */
  const serializeValue = <T extends string | number>(
    value: T,
    defaultValue: T,
  ): string | undefined => {
    if (value === defaultValue) return undefined;
    return String(value);
  };

  // ============================================================================
  // State Setters with URL Sync
  // ============================================================================

  // Search query - debounced URL update
  const setSearchQuery = useCallback(
    (query: string) => {
      setSearchQueryInternal(query);

      // Debounced URL update (300ms after typing stops)
      if (queryUrlTimeoutRef.current) {
        clearTimeout(queryUrlTimeoutRef.current);
      }
      queryUrlTimeoutRef.current = setTimeout(() => {
        updateUrl({ query: query || undefined }, { replace: true });
      }, SEARCH_DEFAULTS.DEBOUNCE_MS);
    },
    [updateUrl],
  );

  // Attributes - immediate URL update
  const setSelectedAttributes = useCallback(
    (attrs: string[]) => {
      setSelectedAttributesInternal(attrs);
      if (defaultsSynced) {
        updateUrl(
          { attributes: serializeArray(attrs, defaults.attributes) },
          { replace: true },
        );
      }
    },
    [updateUrl, defaults.attributes, defaultsSynced],
  );

  // Entity types - immediate URL update
  const setSelectedEntityTypes = useCallback(
    (types: string[]) => {
      setSelectedEntityTypesInternal(types);
      if (defaultsSynced) {
        updateUrl(
          { entityTypes: serializeArray(types, defaults.entityTypes) },
          { replace: true },
        );
      }
    },
    [updateUrl, defaults.entityTypes, defaultsSynced],
  );

  // Date filter - immediate URL update
  const setDateFilter = useCallback(
    (date: string) => {
      setDateFilterInternal(date);
      if (defaultsSynced) {
        updateUrl(
          { dateFilter: serializeValue(date, defaults.dateFilter) },
          { replace: true },
        );
      }
    },
    [updateUrl, defaults.dateFilter, defaultsSynced],
  );

  // Sort by - immediate URL update
  const setSortBy = useCallback(
    (sort: string) => {
      setSortByInternal(sort);
      if (defaultsSynced) {
        updateUrl(
          { sortBy: serializeValue(sort, defaults.sortBy) },
          { replace: true },
        );
      }
    },
    [updateUrl, defaults.sortBy, defaultsSynced],
  );

  // Sort order - immediate URL update
  const setSortOrder = useCallback(
    (order: "asc" | "desc") => {
      setSortOrderInternal(order);
      updateUrl(
        { sortOrder: serializeValue(order, SORT_ORDER.DESC) },
        { replace: true },
      );
    },
    [updateUrl],
  );

  // Search mode - immediate URL update
  const setSearchMode = useCallback(
    (mode: string) => {
      setSearchModeInternal(mode);
      if (defaultsSynced) {
        updateUrl(
          { searchMode: serializeValue(mode, defaults.searchMode) },
          { replace: true },
        );
      }
    },
    [updateUrl, defaults.searchMode, defaultsSynced],
  );

  // Page - push to history (back goes to previous page)
  const setPage = useCallback(
    (newPage: number) => {
      setPageInternal(newPage);
      updateUrl(
        { page: serializeValue(newPage, SEARCH_DEFAULTS.PAGE) },
        { replace: false }, // Push - creates history entry
      );
    },
    [updateUrl],
  );

  // Wrapped setLimit that persists to localStorage and URL
  const setLimit = useCallback(
    (newLimit: number) => {
      setLimitState(newLimit);

      // Persist to localStorage
      try {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(newLimit));
      } catch {
        // localStorage not available
      }

      // Update URL params
      updateUrl(
        { limit: serializeValue(newLimit, SEARCH_DEFAULTS.LIMIT) },
        { replace: true },
      );
    },
    [updateUrl],
  );

  // ============================================================================
  // Browser Navigation Handling
  // ============================================================================

  // Handle back/forward navigation by syncing URL changes to state
  useEffect(() => {
    // Skip on initial mount (already handled by initialUrlState)
    if (!isHydrated) return;

    const currentUrlState = parseUrlState(searchParams);

    // Only update state if URL actually changed from external navigation
    // (not from our own state updates)

    // Check each field and update if different
    if (
      currentUrlState.query !== undefined &&
      currentUrlState.query !== searchQuery
    ) {
      setSearchQueryInternal(currentUrlState.query);
    } else if (currentUrlState.query === undefined && searchQuery !== "") {
      setSearchQueryInternal("");
    }

    if (currentUrlState.page !== undefined && currentUrlState.page !== page) {
      setPageInternal(currentUrlState.page);
    } else if (
      currentUrlState.page === undefined &&
      page !== SEARCH_DEFAULTS.PAGE
    ) {
      setPageInternal(SEARCH_DEFAULTS.PAGE);
    }

    if (
      currentUrlState.attributes !== undefined &&
      !arraysEqual(currentUrlState.attributes, selectedAttributes)
    ) {
      setSelectedAttributesInternal(currentUrlState.attributes);
    }

    if (
      currentUrlState.entityTypes !== undefined &&
      !arraysEqual(currentUrlState.entityTypes, selectedEntityTypes)
    ) {
      setSelectedEntityTypesInternal(currentUrlState.entityTypes);
    }

    if (
      currentUrlState.dateFilter !== undefined &&
      currentUrlState.dateFilter !== dateFilter
    ) {
      setDateFilterInternal(currentUrlState.dateFilter);
    }

    if (
      currentUrlState.sortBy !== undefined &&
      currentUrlState.sortBy !== sortBy
    ) {
      setSortByInternal(currentUrlState.sortBy);
    }

    if (
      currentUrlState.sortOrder !== undefined &&
      currentUrlState.sortOrder !== sortOrder
    ) {
      setSortOrderInternal(currentUrlState.sortOrder);
    }

    if (
      currentUrlState.searchMode !== undefined &&
      currentUrlState.searchMode !== searchMode
    ) {
      setSearchModeInternal(currentUrlState.searchMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Only re-run when URL changes

  // Reset page to 1 when any filter changes
  useEffect(() => {
    // Skip during hydration to avoid resetting page from URL
    if (!isHydrated) return;

    // Only reset if we're not on page 1
    if (page !== SEARCH_DEFAULTS.PAGE) {
      setPageInternal(SEARCH_DEFAULTS.PAGE);
      updateUrl({ page: undefined }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearch,
    selectedAttributes,
    selectedEntityTypes,
    dateFilter,
    sortBy,
    sortOrder,
    searchMode,
    limit,
  ]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (queryUrlTimeoutRef.current) {
        clearTimeout(queryUrlTimeoutRef.current);
      }
    };
  }, []);

  // Auto-switch to relevance sort when a search term is active
  const effectiveSortBy =
    debouncedSearch && sortBy === SORT_BY.UPDATED_AT
      ? SORT_BY.RELEVANCE
      : sortBy;

  // Map frontend slug format to API param format
  const mapSortByToApi = (slug: string): string => {
    return SORT_BY_TO_API[slug] ?? "updated_at";
  };

  // Map date filter slug to API param value.
  // Slugs use hyphens (matching DB filter_allowed_values): this-week, this-month.
  // "all-time" maps to undefined (omit the parameter).
  const mapDateFilterToApi = (slug: string): string | undefined => {
    if (slug === DATE_FILTERS.ALL_TIME) return undefined;
    // Pass through as-is: "today", "this-week", "this-month"
    return slug;
  };

  // Build query parameters
  const queryParams = {
    q: debouncedSearch || undefined,
    searchMode: searchMode !== SEARCH_MODES.SMART ? searchMode : undefined,
    projectId: projectId || undefined,
    entityTypes:
      selectedEntityTypes.length > 0 ? selectedEntityTypes : undefined,
    attributes: selectedAttributes.length > 0 ? selectedAttributes : undefined,
    dateFilter: mapDateFilterToApi(dateFilter),
    sortBy: mapSortByToApi(effectiveSortBy),
    sortOrder,
    page,
    limit,
  };

  const query = useQuery<{
    results: SearchResultItem[];
    meta: SearchPaginationMeta;
  }>({
    queryKey: [
      "search",
      projectId ?? null,
      debouncedSearch,
      selectedAttributes,
      selectedEntityTypes,
      dateFilter,
      effectiveSortBy,
      sortOrder,
      searchMode,
      page,
      limit,
    ],
    queryFn: async () => {
      // Build URL query string
      const params = new URLSearchParams();
      if (queryParams.q) params.set("q", queryParams.q);
      if (queryParams.searchMode)
        params.set("searchMode", queryParams.searchMode);
      if (queryParams.projectId) params.set("projectId", queryParams.projectId);
      if (queryParams.entityTypes) {
        params.set("entityTypes", queryParams.entityTypes.join(","));
      }
      if (queryParams.attributes) {
        params.set("attributes", queryParams.attributes.join(","));
      }
      if (queryParams.dateFilter)
        params.set("dateFilter", queryParams.dateFilter);
      if (queryParams.sortBy) params.set("sortBy", queryParams.sortBy);
      if (queryParams.sortOrder) params.set("sortOrder", queryParams.sortOrder);
      params.set("page", String(queryParams.page));
      params.set("limit", String(queryParams.limit));

      const queryString = params.toString();
      const url = queryString
        ? `${SEARCH_API_PATHS.SEARCH}?${queryString}`
        : SEARCH_API_PATHS.SEARCH;

      const response = await client.get<SearchResponse>({
        url,
        security: [{ scheme: "bearer", type: "http" }],
      });

      const result = response.data as SearchResponse | undefined;
      // API returns meta as { dbAt, pagination: { page, limit, total, ... } }
      const pagination = (result?.meta as unknown as Record<string, unknown>)
        ?.pagination as SearchPaginationMeta | undefined;
      return {
        results: result?.data ?? [],
        meta: pagination ?? {
          page: SEARCH_DEFAULTS.PAGE,
          limit: SEARCH_DEFAULTS.LIMIT,
          total: 0,
          hasNext: false,
          hasPrev: false,
        },
      };
    },
    enabled: !!user,
    staleTime: 2 * 60 * 1000, // 2 minutes
    placeholderData: keepPreviousData,
  });

  const clearFilters = useCallback(() => {
    setSelectedAttributesInternal(defaults.attributes);
    setSelectedEntityTypesInternal(defaults.entityTypes);
    setDateFilterInternal(defaults.dateFilter);
    setSortByInternal(defaults.sortBy);
    setSortOrderInternal(SORT_ORDER.DESC);
    setSearchModeInternal(defaults.searchMode);
    setSearchQueryInternal("");
    setPageInternal(SEARCH_DEFAULTS.PAGE);

    // Clear all filter-related URL params
    updateUrl(
      {
        query: undefined,
        attributes: undefined,
        entityTypes: undefined,
        dateFilter: undefined,
        sortBy: undefined,
        sortOrder: undefined,
        searchMode: undefined,
        page: undefined,
      },
      { replace: true },
    );
  }, [defaults, updateUrl]);

  // Check if any filter is non-default (memoised to avoid recalculation on every render)
  // While defaults are still loading, report false to avoid flashing search results on refresh
  // Note: sortBy and sortOrder are excluded - they affect display order, not which results to show
  const hasActiveFilters = useMemo(() => {
    if (!defaultsSynced) return false;
    if (searchQuery !== "") return true;
    if (dateFilter !== defaults.dateFilter) return true;
    if (searchMode !== defaults.searchMode) return true;

    const sortedSelected = [...selectedAttributes].sort();
    const sortedDefaults = [...defaults.attributes].sort();
    if (
      sortedSelected.length !== sortedDefaults.length ||
      sortedSelected.some((v, i) => v !== sortedDefaults[i])
    ) {
      return true;
    }

    const sortedEntityTypes = [...selectedEntityTypes].sort();
    const sortedDefaultEntityTypes = [...defaults.entityTypes].sort();
    if (
      sortedEntityTypes.length !== sortedDefaultEntityTypes.length ||
      sortedEntityTypes.some((v, i) => v !== sortedDefaultEntityTypes[i])
    ) {
      return true;
    }

    return false;
  }, [
    defaultsSynced,
    searchQuery,
    selectedAttributes,
    selectedEntityTypes,
    dateFilter,
    sortBy,
    sortOrder,
    searchMode,
    defaults,
  ]);

  return {
    // Search text
    searchQuery,
    setSearchQuery,
    debouncedSearch,

    // Filter state
    selectedAttributes,
    setSelectedAttributes,
    selectedEntityTypes,
    setSelectedEntityTypes,
    dateFilter,
    setDateFilter,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    searchMode,
    setSearchMode,
    effectiveSortBy,

    // Advanced panel
    isAdvancedOpen,
    setIsAdvancedOpen,

    // Filter config
    filters,
    isLoadingFilters,

    // Actions
    clearFilters,
    hasActiveFilters,

    // Pagination
    page,
    setPage,
    limit,
    setLimit,
    total: query.data?.meta?.total ?? 0,
    totalPages: query.data?.meta
      ? Math.max(1, Math.ceil(query.data.meta.total / query.data.meta.limit))
      : 1,

    // Query results
    results: query.data?.results ?? [],
    meta: query.data?.meta ?? null,
    isSearching: query.isFetching,
    isFetching: query.isFetching, // Alias for UI loading states
    isLoading: query.isLoading,
    error: query.error,

    // URL sync state
    isHydrated,
  };
}
